import { create } from 'zustand';

import type { CurrentUser } from '../api/auth';

interface AuthState {
  user: CurrentUser | null;
  /** Distinguishes "not loaded yet" from "loaded, nobody signed in". */
  resolved: boolean;
  setUser: (user: CurrentUser | null) => void;
  markResolved: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  resolved: false,
  setUser: (user) => set({ user, resolved: true }),
  markResolved: () => set({ resolved: true }),
}));

/**
 * True when a real account is signed in — which is the question the public pages ask.
 *
 * The public site used to ask nothing: every page outside the app offered "Create an account" to
 * people who already had one and were signed into it, and the sign-up page obliged them. Someone
 * who followed that button and came back in through Google landed in the onboarding wizard, and it
 * looked exactly as if the account had been emptied.
 *
 * A demo session does not count. A demo visitor is still a stranger being asked to sign up, and
 * the demo banner sends them to the sign-up page on purpose.
 *
 * False until the session is known, so a public page paints what a stranger sees — the usual case,
 * and what its pre-rendered head describes — and changes once, for the few who are signed in.
 */
export function useHasAccount(): boolean {
  return useAuthStore((s) => s.user !== null && !s.user.isDemo);
}
